import { COMPANY_ID_HEADER } from '@helix/auth';
import {
  getAuthHeaders,
  TENANT_ID_LOCAL_STORAGE_KEY,
  USER_ID_HEADER,
  USER_ID_LOCAL_STORAGE_KEY,
} from './graphql-client';
import { resolveConfiguredOrLocalApiBaseUrl } from './local-api-origin';
import { buildLoginRedirectPath, sanitizeAuthNextPath, type AuthLogoutResponse } from '@helix/lib';

export type AuthMeResponse = {
  companyId: string;
  userId: string;
  roles: string[];
  profile?: {
    role?: string | null;
  } | null;
};

let cachedAuthMe: AuthMeResponse | null = null;
let cachedAuthMeAtMs = 0;
let authMeInflight: Promise<AuthMeResponse | null> | null = null;
const AUTH_ME_CACHE_TTL_MS = 10_000;

function persistBrowserIdentity(identity: AuthMeResponse | null): void {
  if (typeof window === 'undefined') return;
  if (!identity?.companyId || !identity?.userId) return;

  try {
    window.localStorage.setItem(TENANT_ID_LOCAL_STORAGE_KEY, identity.companyId);
    window.localStorage.setItem(USER_ID_LOCAL_STORAGE_KEY, identity.userId);
  } catch {
    // Ignore storage failures; cookie-backed auth remains the source of truth.
  }
}

async function fetchAuthEndpoint(path: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  const extraHeaders = getAuthHeaders();
  for (const [key, value] of Object.entries(extraHeaders)) {
    if (!headers.has(key)) {
      headers.set(key, value);
    }
  }

  return fetch(`${getApiBaseUrl()}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  });
}

export function getApiBaseUrl(): string {
  return resolveConfiguredOrLocalApiBaseUrl(process.env.NEXT_PUBLIC_API_URL, {
    nodeEnv: process.env.NODE_ENV,
  });
}

export function clearAuthMeCache(): void {
  cachedAuthMe = null;
  cachedAuthMeAtMs = 0;
  authMeInflight = null;
}

export async function authMe(): Promise<AuthMeResponse | null> {
  if (cachedAuthMe && Date.now() - cachedAuthMeAtMs < AUTH_ME_CACHE_TTL_MS) {
    return cachedAuthMe;
  }

  if (authMeInflight) return authMeInflight;

  authMeInflight = (async () => {
    const response = await fetchAuthEndpoint('/auth/me', { cache: 'no-cache' });
    if (response.status === 401) {
      return null;
    }
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to load auth context');
    }
    const me = (await response.json()) as AuthMeResponse;
    cachedAuthMe = me;
    cachedAuthMeAtMs = Date.now();
    persistBrowserIdentity(me);
    return me;
  })();

  try {
    return await authMeInflight;
  } finally {
    authMeInflight = null;
  }
}

export async function requireAuthMe(): Promise<AuthMeResponse> {
  const me = await authMe();
  if (!me) {
    throw new Error('Not authenticated');
  }
  return me;
}

export async function resolveBrowserAuthHeaders(): Promise<Record<string, string>> {
  if (typeof window === 'undefined') {
    return {};
  }

  const headers = getAuthHeaders();
  if (headers[COMPANY_ID_HEADER] && headers[USER_ID_HEADER]) {
    return headers;
  }

  const me = await authMe();
  if (!me?.companyId || !me?.userId) {
    return headers;
  }

  return {
    ...headers,
    [COMPANY_ID_HEADER]: me.companyId,
    [USER_ID_HEADER]: me.userId,
  };
}

export async function logout(next?: string): Promise<AuthLogoutResponse> {
  const nextPath = sanitizeAuthNextPath(next);
  const response = await fetchAuthEndpoint('/auth/logout', {
    method: 'POST',
    body: JSON.stringify({ next: nextPath }),
    headers: { 'content-type': 'application/json' },
  });

  if (response.status === 401) {
    clearAuthMeCache();
    return {
      ok: true,
      redirectTo: buildLoginRedirectPath(nextPath, { signedOut: true, freshAuth: true }),
      providerLogoutUrl: null,
      freshAuth: true,
      nextPath,
    };
  }

  if (!response.ok && response.status !== 401) {
    const text = await response.text();
    throw new Error(text || 'Logout failed');
  }

  const payload = (await response.json()) as AuthLogoutResponse;
  clearAuthMeCache();
  return payload;
}
